import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  where, 
  addDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db, functions, getNextPoNumber, addOrderToDb } from '../firebase';
import { 
  ORDER_STATUSES,
  LINE_STATUSES,
  LINE_TYPES,
  SIZES,
  sortSizes 
} from '../firebase/schema';
import LineItem2 from '../components/LineItem2';
import { duplicateLineItem } from '../utils/duplicateLineItem';
import { moveLineInArray } from '../utils/reorderLineItems';
import AddCustomer from '../components/AddCustomer';
import FeesModal from '../components/FeesModal';
import { EMPLOYEE_ROLES } from '../constants/employeeRoles';
import SuccessAnimation from '../components/SuccessAnimation';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage } from '../firebase';
import { computeSalesTax, sumTaxableFeesAmount } from '../utils/orderSalesTax';
import { enrichCustomerDocForCreate } from '../utils/customerPortal';
import { useFacility } from '../context/FacilityContext';
import { FACILITIES, resolveFacilityId } from '../utils/facilities';

const generateUniqueId = (() => {
  let counter = 0;
  return (prefix = '') => {
    counter += 1;
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
  };
})();

const generatePMKey = (pm, index) => {
  if (typeof pm === 'object' && pm.id) {
    return `pm-${pm.id}`;
  }
  if (typeof pm === 'object' && pm.name) {
    return `pm-name-${pm.name.replace(/\s+/g, '-').toLowerCase()}`;
  }
  if (typeof pm === 'string') {
    return `pm-str-${pm.replace(/\s+/g, '-').toLowerCase()}-${index}`;
  }
  return `pm-fallback-${index}`;
};

const deduplicatePMs = (pms) => {
  if (!Array.isArray(pms)) return [];
  
  const seen = new Set();
  return pms.filter(pm => {
    const value = typeof pm === 'object' ? pm.name : pm;
    const duplicate = seen.has(value);
    seen.add(value);
    return !duplicate;
  });
};

// Helper function to check if any instructions are active
const hasActiveInstructions = (instructions) => {
  if (!instructions) return false;
  return instructions.shipping || instructions.sorting || instructions.folding ||
         instructions.bagging || instructions.fulfillment || instructions.neckTag ||
         instructions.taxExempt || instructions.designWorkOnly || instructions.doNotBill ||
         instructions.followUpEmail || instructions.instagramWorthy || instructions.providedGoods ||
         instructions.sample ||
         !!instructions.other;
};

const PageContainer = styled.div`
  padding: 0;
  text-transform: uppercase;
  background: var(--al-bg);
  min-height: 100%;
  height: 100%;
  color: white;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  
  input, textarea {
    text-transform: uppercase;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 0.5rem;
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.6rem;
`;

const SaveButton = styled(Button)`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
`;

const Section = styled.div`
  background: ${props => props.theme.colors.navbar};
  border: none;
  border-radius: 0;
  padding: 20px;
  margin-bottom: 20px;
  margin-left: 20px;
  margin-right: 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  margin: 30px 20px 0 20px;
  align-items: flex-start;
  position: relative;
`;



const LinesSection = styled(Section)`
  margin-top: 80px;
  padding-top: 12px;
  padding-bottom: 80px; // Extra space for "Add Line" button at bottom
  max-height: calc(100vh - 320px); // Account for header + footer space
  overflow-y: auto;
  margin-bottom: 20px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--al-bg);
    border-radius: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 0;
    
    &:hover {
      background: #5a5a5a;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #4a4a4a var(--al-bg);
  
  .line-item {
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 0;
    margin-bottom: 8px;
  }
`;

const FooterSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  background: ${props => props.theme.colors.navbar};
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  padding: 16px 20px;
  box-sizing: border-box;
  margin-top: auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FooterBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 120px;
`;

const NotesFooterBox = styled(FooterBox)`
  flex: 2;
  min-width: 200px;
`;

const UpchargeFooterBox = styled(FooterBox)`
  flex: 1;
`;

const FeesFooterBox = styled(FooterBox)`
  flex: 1;
  
  .header {
    padding: 4px;
    font-weight: 600;
    font-size: 0.85em;
  }
  
  .summary {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
    flex: 1;
  }
  
  .fee-line {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
  }
  
  .fee-type {
    color: rgba(255, 255, 255, 0.8);
  }
  
  .fee-amount {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
  }
  
  .instructions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .instruction-tag {
    background: ${props => props.theme.colors.primary};
    color: #000;
    padding: 2px 6px;
    font-size: 0.7em;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const TotalsFooterBox = styled(FooterBox)`
  flex: 1;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  .header {
    padding: 4px;
    font-weight: 700;
    font-size: 0.9em;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  
  .label {
    min-width: 45px;
    width: 45px;
    text-align: right;
    font-size: 0.85em;
    flex-shrink: 0;
    color: white;
    font-weight: 600;
  }
  
  input {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: ${props => props.theme.colors.primary};
    padding: 6px 8px;
    flex: 1;
    min-width: 60px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
  }
`;

const EditFeesBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.75em;
  margin-top: auto;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const NotesBox = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 12px;
  margin: 0;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  resize: none;
  text-transform: uppercase;
  box-sizing: border-box;
  flex: 1;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;


const CustomerSearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.6rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchResultItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  text-transform: uppercase;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const AddNewButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: ${props => props.theme.colors.primary};
  color: #000000;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  
  &:hover {
    opacity: 0.9;
  }
`;

const DateContainer = styled.div`
  display: flex;
  gap: 20px;

  div {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
      white-space: nowrap;
    }
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 6px;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.6rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

const AddLineButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const AddLineButton = styled.button`
  background: var(--al-primary);
  color: #000000;
  border: none;
  border-radius: 0;
  padding: 0.5rem 1rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  cursor: pointer;
  text-transform: uppercase;
  
  &:hover {
    opacity: 0.9;
  }
`;


const SelectContainer = styled.div`
  width: 140px;
  flex-shrink: 0;
`;

const FacilitySelectContainer = styled.div`
  width: 160px;
  flex-shrink: 0;
`;

const ProjectManagerSelect = styled.select`
  width: 100%;
  padding: 6px;
  background: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.border};
  color: #000;
  border-radius: 0;
  cursor: pointer;
  font-size: 0.6rem;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  
  option {
    background: white;
    color: black;
    font-size: 0.6rem;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primaryDark};
  }
`;

const StatusSelect = styled.select`
  width: 100%;
  padding: 6px;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 4px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.6rem;
  
  option {
    background: #363636;
    color: white;
  }
`;

const CreateOrderButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: var(--al-bg);
  border: none;
  padding: 8px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  font-weight: 600;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const EmbleoPromptOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const EmbleoPromptModal = styled.div`
  width: min(460px, calc(100vw - 32px));
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 18px;
  color: white;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
`;

const EmbleoPromptTitle = styled.div`
  color: var(--al-primary);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
`;

const EmbleoPromptText = styled.div`
  font-size: 0.78rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 14px;
`;

const EmbleoPromptActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const EmbleoPromptButton = styled.button`
  border: 1px solid ${props => props.primary ? 'var(--al-primary)' : 'rgba(255,255,255,0.35)'};
  background: ${props => props.primary ? 'var(--al-primary)' : 'transparent'};
  color: ${props => props.primary ? 'var(--al-bg)' : 'white'};
  padding: 7px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;


const addBusinessDays = (date, days) => {
  let currentDate = new Date(date);
  let addedDays = 0;
  
  while (addedDays < days) {
    currentDate.setDate(currentDate.getDate() + 1);
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      addedDays++;
    }
  }
  
  return currentDate;
};

// Helper function to format minutes as HH:MM
const formatMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const isEmbroideryLineType = (value) => String(value || '').trim().toLowerCase() === 'embroidery';

const NewOrder = ({ initialCustomerName, initialCustomerId, reorderDesigns, onClose, onOrderCreated }) => {
  const { facilityId: activeFacilityId } = useFacility();
  const navigate = useNavigate();

  const exitWithoutSave = () => {
    if (onClose) onClose();
    else navigate('/orders');
  };

  const exitAfterOrderCreated = () => {
    if (onOrderCreated) onOrderCreated();
    else navigate('/orders', { state: { fromNewOrder: true } });
  };
  const today = new Date();
  const defaultDueDate = addBusinessDays(today, 8);
  
  const [lines, setLines] = useState(() => {
    if (reorderDesigns && reorderDesigns.length > 0) {
      console.log('Creating lines from reorder designs:', reorderDesigns);
      return reorderDesigns.map((design, index) => ({
        isExpanded: index === 0, // Only expand the first line
        id: generateUniqueId('line'),
        type: design.type || LINE_TYPES.SCREEN_PRINT, // Preserve type from reorder
        design: design.design || '',
        style: design.style || '',
        color: design.color || '',
        sizeType: design.sizeType || 'ADULT',
        sizes: Object.fromEntries(SIZES[design.sizeType || 'ADULT'].map(size => [size, ''])),
        frontInkColors: design.frontInkColors || [],
        backInkColors: design.backInkColors || [],
        otherInkColors: design.otherInkColors || [],
        ...(design.pressSetup && { pressSetup: design.pressSetup }),
        price: design.price || '',
        cogs: design.cogs || '',
        designFileUrl: design.designFileUrl || '',
        files: {
          ...(design.files || {}),
          production: Array.isArray(design?.files?.production) ? design.files.production : []
        }
      }));
    }
    return [{
      isExpanded: true, // Expand the first line in a new order
      id: generateUniqueId('line'),
      type: LINE_TYPES.SCREEN_PRINT, // Default type for new orders
      design: '',
      style: '',
      color: '',
      sizeType: 'ADULT',
      sizes: Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
      frontInkColors: [],
      backInkColors: [],
      otherInkColors: [],
      price: '',
      cogs: '',
      artworkUrl: ''
    }];
  });

  const [orderData, setOrderData] = useState({
    customerName: initialCustomerName || '',
    customerId: initialCustomerId || '',
    projectManager: '',
    facilityId: activeFacilityId,
    startDate: today.toISOString().split('T')[0],
    dueDate: defaultDueDate.toISOString().split('T')[0],
    notes: '',
    fee1Type: '',
    fee1Quantity: '',
    fee1Price: '',
    fee2Type: '',
    fee2Quantity: '',
    fee2Price: '',
    upchargePrice: '2.00',
    discount: { type: 'fixed', amount: '' }
  });
  
  // New state for fees modal and additional instructions
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [fees, setFees] = useState([]);
  const [shippingDetails, setShippingDetails] = useState(null);
  const [additionalInstructions, setAdditionalInstructions] = useState({
    shipping: false,
    sorting: false,
    folding: false,
    bagging: false,
    fulfillment: false,
    neckTag: false,
    other: '',
    taxExempt: false,
    designWorkOnly: false,
    followUpEmail: false,
    doNotBill: false,
    instagramWorthy: false,
    providedGoods: false,
    sample: false
  });
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [projectManagers, setProjectManagers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [customerSearch, setCustomerSearch] = useState(initialCustomerName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmbleoPrompt, setShowEmbleoPrompt] = useState(false);
  const [pendingEmbleoOrderId, setPendingEmbleoOrderId] = useState('');
  const [isSendingToEmbleo, setIsSendingToEmbleo] = useState(false);
  const [embleoSendError, setEmbleoSendError] = useState('');

  const effectiveCustomerId = useMemo(() => {
    if (orderData.customerId) return orderData.customerId;
    const query = (customerSearch || orderData.customerName || '').trim().toLowerCase();
    if (!query || !customers.length) return '';
    const match = customers.find((c) => (c.client || '').trim().toLowerCase() === query);
    return match?.id || '';
  }, [orderData.customerId, orderData.customerName, customerSearch, customers]);

  useEffect(() => {
    const fetchCustomers = async () => {
      const q = query(collection(db, 'customers'), orderBy('client'));
      const querySnapshot = await getDocs(q);
      const customersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchProjectManagers = async () => {
      const q = query(
        collection(db, 'employees'), 
        where('role', '==', EMPLOYEE_ROLES.PROJECT_MANAGER)
      );
      
      const querySnapshot = await getDocs(q);
      const managersData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(manager => manager.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setProjectManagers(managersData);
    };

    fetchProjectManagers();
  }, []);

  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    if (query.trim() === '') {
      setFilteredCustomers([]);
      return;
    }
    const filtered = customers.filter(customer =>
      customer.client.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  const handleCustomerSelect = (customer) => {
    setOrderData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.client
    }));
    setCustomerSearch(customer.client);
    setFilteredCustomers([]);
  };

  const handleCustomerSearchBlur = () => {
    if (orderData.customerId) return;
    const query = customerSearch.trim().toLowerCase();
    if (!query) return;
    const match = customers.find((c) => (c.client || '').trim().toLowerCase() === query);
    if (match) handleCustomerSelect(match);
  };

  const handleAddNewCustomer = async (newCustomer) => {
    try {
      const docRef = await addDoc(collection(db, 'customers'), enrichCustomerDocForCreate(newCustomer));
      const customerWithId = { id: docRef.id, ...newCustomer };
      setCustomers(prev => [...prev, customerWithId]);
      handleCustomerSelect(customerWithId);
      setShowAddCustomer(false);
    } catch (error) {
      alert('Error adding customer. Please try again.');
    }
  };

  const handleFeesModalSave = (data) => {
    setFees(data.fees);
    setAdditionalInstructions(data.additionalInstructions || {});
    setShippingDetails(data.shippingDetails || null);
    setOrderData(prev => ({
      ...prev,
      discount: data.discount || { type: 'fixed', amount: '' },
      ...('upchargePrice' in data && { upchargePrice: data.upchargePrice }),
      ...('customerPoNumber' in data && { customerPoNumber: data.customerPoNumber })
    }));
  };

  const calculateTotalFees = () => {
    return fees.reduce((total, fee) => {
      return total + (parseFloat(fee.quantity) || 0) * (parseFloat(fee.price) || 0);
    }, 0);
  };

  const handleAddLine = () => {
    setLines(prevLines => [...prevLines, {
      isExpanded: false,
      id: generateUniqueId('line'),
      type: LINE_TYPES.SCREEN_PRINT, // Default type for new lines
      design: '',
      style: '',
      color: '',
      sizeType: 'ADULT',
      sizes: Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
      frontInkColors: [],
      backInkColors: [],
      otherInkColors: [],
      pricePerPiece: '',
      cogs: '',
      mockupUrl: ''
    }]);
  };

  const handleCancel = () => {
    exitWithoutSave();
  };

  const handleSave = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      console.log('Starting order creation with data:', {
        orderData,
        lines,
        customerSearch
      });

      // Get next PO number
      const poNumber = await getNextPoNumber();
      console.log('Generated PO Number:', poNumber);

      // Upload artwork files and update line items
      const updatedLines = await Promise.all(lines.map(async (line) => {
        let updatedLine = { ...line };
        
        // Check for artwork file in multiple locations (LineItem2 stores in _mockupFile or files.mockup.file)
        const artworkFile = line.artworkFile || line._mockupFile || line.files?.mockup?.file;
        
        if (artworkFile) {
          console.log('Uploading mockup artwork for line:', line.id);
          const storageRef = ref(storage, `artwork/${poNumber}/${artworkFile.name}`);
          await uploadBytes(storageRef, artworkFile);
          const url = await getDownloadURL(storageRef);
          updatedLine.designFileUrl = url;
        }
        
        // Upload production files for DTF/DTG lines (files with blob URLs need to be uploaded)
        if (line.files?.production && Array.isArray(line.files.production)) {
          const uploadedProductionFiles = await Promise.all(
            line.files.production.map(async (prodFile) => {
              // Skip if already has a real URL (not blob)
              if (prodFile.url && !prodFile.url.startsWith('blob:')) {
                return prodFile;
              }
              
              // Upload the file if we have the File object
              if (prodFile.file) {
                console.log('Uploading production file for line:', line.id, prodFile.name);
                const sanitizedDesignName = (line.design || line.designName || 'unnamed').replace(/[^a-zA-Z0-9-_]/g, '_');
                const storageRef = ref(storage, `artwork/${poNumber}/${sanitizedDesignName}/Production/${prodFile.type || 'unassigned'}/${prodFile.file.name}`);
                await uploadBytes(storageRef, prodFile.file);
                const url = await getDownloadURL(storageRef);
                return {
                  name: prodFile.name,
                  url: url,
                  type: prodFile.type || 'unassigned'
                };
              }
              
              // If no file object and blob URL, skip it (can't upload)
              console.warn('⚠️ Production file has blob URL but no File object, skipping:', prodFile.name);
              return null;
            })
          );
          
          // Filter out nulls and update line
          const validProductionFiles = uploadedProductionFiles.filter(f => f !== null);
          if (validProductionFiles.length > 0) {
            updatedLine.files = {
              ...updatedLine.files,
              production: validProductionFiles
            };
          }
        }
        
        return updatedLine;
      }));

      console.log('Updated lines with artwork:', updatedLines);

      const providedGoods = additionalInstructions?.providedGoods === true;
      const isSample = additionalInstructions?.sample === true;
      const orderPayload = {
        poNumber,
        projectManager: orderData.projectManager,
        facilityId: resolveFacilityId(orderData.facilityId || activeFacilityId),
        customerId: orderData.customerId,
        customerName: orderData.customerName,
        startDate: serverTimestamp(),
        dueDate: orderData.dueDate,
        notes: orderData.notes || '',
        status: providedGoods && !isSample ? ORDER_STATUSES.ORDERED : ORDER_STATUSES.NEW,
        createdAt: serverTimestamp(),
        readyToOrder: false,
        readyToOrderDate: null,
        printedLines: 0,
        printedQuantity: 0,
        totalLines: lines.length,
        lineRefs: [], // This will be populated by addOrderToDb
        providedGoods,
        ...(isSample && { sample: true }),
        ...('upchargePrice' in orderData && orderData.upchargePrice !== undefined && { upchargePrice: orderData.upchargePrice }),
        lines: updatedLines.map((line, index) => {
          // Process sizes to ensure all quantities are numbers
          const processedSizes = {};
          if (line.sizes) {
            Object.entries(line.sizes).forEach(([size, quantity]) => {
              processedSizes[size] = Number(quantity) || 0;
            });
          }

          // Process ink colors
          const frontInkColors = Array.isArray(line.frontInkColors) ? line.frontInkColors.filter(color => color) : [];
          const backInkColors = Array.isArray(line.backInkColors) ? line.backInkColors.filter(color => color) : [];
          const otherInkColors = Array.isArray(line.otherInkColors) ? line.otherInkColors.filter(color => color) : [];

          const totalQuantity = Object.values(processedSizes).reduce((sum, qty) => sum + qty, 0);
          const pricePerPiece = Number(line.price) || 0;
          const cogs = Number(line.cogs) || 0;

          // Explicitly preserve line type from state (DTF, DTG, Screen Print, etc.) so it is not lost when saving
          const rawType = line.type != null ? String(line.type).trim() : '';
          const lineType = rawType !== '' ? rawType : LINE_TYPES.SCREEN_PRINT;

          return {
          designName: line.design || '',
          garmentId: line.style || '',
          garmentColor: line.color || '',
            sizes: processedSizes,
            pricePerPiece,
            cogs,
          status: 'pending',
            frontInkColors,
            backInkColors,
            otherInkColors,
            printLocations: line.printLocations || [],
          sizeType: line.sizeType || 'unisex',
            totalQuantity,
            totalLinePrice: totalQuantity * pricePerPiece,
            totalLineCOGS: totalQuantity * cogs,
            lineNumber: index + 1,
            type: lineType,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
            updatedBy: 'currentUser',
            designFileUrl: line.designFileUrl || null,
            files: line.files || undefined,
            ...(line.pressSetup && { pressSetup: line.pressSetup }),
            ...(line.inkNotes && { inkNotes: line.inkNotes })
          };
        }),
        fees: fees,
        discount: orderData.discount || { type: 'fixed', amount: '' },
        additionalInstructions: additionalInstructions,
        shippingDetails,
        totalQuantity: lines.reduce((sum, line) => 
          sum + Object.values(line.sizes || {}).reduce((lineSum, qty) => lineSum + (Number(qty) || 0), 0), 0
        ),
        subtotal: calculatePricing().orderTotal,
        orderTotal: calculatePricing().orderTotal,
        cogsTotal: calculatePricing().cogsTotal,
        cogsPercentage: calculatePricing().cogsPercentage,
        extraFees: calculatePricing().feesTotal
      };

      console.log('Sending order payload:', orderPayload);

      const result = await addOrderToDb(orderPayload);
      console.log('Order creation result:', result);

      if (result.success) {
        const hasEmbroideryLine = updatedLines.some((line) => isEmbroideryLineType(line.type));
        setPendingEmbleoOrderId(result.orderId || '');
        setEmbleoSendError('');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setIsSubmitting(false);
          if (hasEmbroideryLine) {
            setShowEmbleoPrompt(true);
          } else {
            exitAfterOrderCreated();
          }
        }, 2000);
      } else {
        alert('Error creating order: ' + result.error);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order: ' + error.message);
      setIsSubmitting(false);
    }
  };

  const handleEmbleoDecision = async (sendToEmbleo) => {
    if (!sendToEmbleo) {
      setShowEmbleoPrompt(false);
      exitAfterOrderCreated();
      return;
    }
    if (!pendingEmbleoOrderId || isSendingToEmbleo) return;
    try {
      setIsSendingToEmbleo(true);
      setEmbleoSendError('');
      const sendOrderEmbroideryToEmbleo = httpsCallable(functions, 'sendOrderEmbroideryToEmbleo');
      const response = await sendOrderEmbroideryToEmbleo({ orderId: pendingEmbleoOrderId });
      const payload = response?.data || {};
      setShowEmbleoPrompt(false);
      alert(`Embleo send complete: ${payload.sent || 0} sent, ${payload.failed || 0} failed.`);
      exitAfterOrderCreated();
    } catch (error) {
      console.error('Error sending embroidery lines to Embleo:', error);
      const rawMessage = error?.message || '';
      const errorCode = error?.code || '';
      if (errorCode === 'functions/internal') {
        setEmbleoSendError(
          'Embleo sync function is not reachable yet. Deploy functions and set EMBLEO_BASE_URL/EMBLEO_API_KEY secrets, then try again.'
        );
      } else setEmbleoSendError(rawMessage || 'Failed to send lines to Embleo');
    } finally {
      setIsSendingToEmbleo(false);
    }
  };

  // Helper function to calculate total quantity
  const calculateTotalQuantity = (sizes) => {
    if (!sizes) return 0;
    return Object.values(sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  };

  const handleKeyDown = (e) => {
    // Prevent form submission on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there's exactly one filtered customer, select it
      if (filteredCustomers.length === 1) {
        handleCustomerSelect(filteredCustomers[0]);
      }
    }

    // Close dropdown on Escape
    if (e.key === 'Escape') {
      setFilteredCustomers([]);
    }
  };

  const handleLineUpdate = (index, updatedLine) => {
    setLines(prevLines => {
      // Prevent unnecessary updates
      if (JSON.stringify(prevLines[index]) === JSON.stringify(updatedLine)) {
        return prevLines;
      }
      
      const newLines = [...prevLines];
      newLines[index] = {
        ...newLines[index],
        ...updatedLine,
        id: newLines[index].id // Preserve the original ID
      };
      return newLines;
    });
  };

  const handleMoveLine = (index, delta) => {
    setLines((prev) => moveLineInArray(prev, index, delta));
  };

  const calculate2XLPlus = () => {
    return lines.reduce((total, line) => {
      // Get upcharge quantities from the sizes object
      const upchargeQuantity = (
        Number(line.sizes?.['2XL'] || 0) +
        Number(line.sizes?.['3XL'] || 0) +
        Number(line.sizes?.['4XL'] || 0) +
        Number(line.sizes?.['5XL'] || 0)
      );
      
      return total + upchargeQuantity;
    }, 0);
  };

  const calculateTotalPieces = () => {
    return lines.reduce((total, line) => {
      // Calculate total from all sizes in the line
      const linePieces = Object.values(line.sizes || {})
        .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
      
      return total + linePieces;
    }, 0);
  };

  // Add helper functions for calculations
  const calculatePrintMinutes = (line) => {
    const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    const inkCount = (line.frontInkColors?.length || 0) + 
                    (line.backInkColors?.length || 0) + 
                    (line.otherInkColors?.length || 0);
    
    // Base setup time (15 minutes per color)
    const setupTime = inkCount * 15;
    
    // Print time (assume 240 pieces per hour = 4 pieces per minute)
    const printTime = quantity / 4;
    
    return setupTime + printTime;
  };

  const calculatePrintRevenue = (line) => {
    const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    const price = Number(line.price) || 0;
    const cogs = Number(line.cogs) || 0;
    
    if (quantity <= 0 || price <= 0) return 0;
    
    const revenuePerPiece = Math.max(0, price - cogs);
    return quantity * revenuePerPiece;
  };

  const calculatePressRate = (revenue, minutes) => {
    if (!revenue || !minutes || minutes <= 0) return 0;
    const hours = minutes / 60;
    return revenue / hours;
  };

  const calculatePricing = () => {
    const lineTotals = lines.map(line => {
      // Process sizes based on type
      let processedSizes = {};
      
      // Handle Design Only lines
      if (line.type === LINE_TYPES.DESIGN_ONLY) {
        processedSizes = {
          'Design': Number(line.sizes?.Design || 0)
        };
      } else {
        // Handle regular print types
        switch(line.sizeType) {
          case 'youth':
            processedSizes = {
              'YXS': Number(line.sizes?.YXS || 0),
              'YS': Number(line.sizes?.YS || 0),
              'YM': Number(line.sizes?.YM || 0),
              'YL': Number(line.sizes?.YL || 0),
              'YXL': Number(line.sizes?.YXL || 0)
            };
            break;
          case 'ladies':
            processedSizes = {
              'XS': Number(line.sizes?.XS || 0),
              'S': Number(line.sizes?.S || 0),
              'M': Number(line.sizes?.M || 0),
              'L': Number(line.sizes?.L || 0),
              'XL': Number(line.sizes?.XL || 0),
              '2XL': Number(line.sizes?.['2XL'] || 0),
              '3XL': Number(line.sizes?.['3XL'] || 0)
            };
            break;
          default: // unisex/adult
            processedSizes = {
              'XS': Number(line.sizes?.XS || 0),
              'S': Number(line.sizes?.S || 0),
              'M': Number(line.sizes?.M || 0),
              'L': Number(line.sizes?.L || 0),
              'XL': Number(line.sizes?.XL || 0),
              '2XL': Number(line.sizes?.['2XL'] || 0),
              '3XL': Number(line.sizes?.['3XL'] || 0),
              '4XL': Number(line.sizes?.['4XL'] || 0),
              '5XL': Number(line.sizes?.['5XL'] || 0)
            };
        }
      }

      // Calculate total quantity from processed sizes
      const totalQuantity = Object.values(processedSizes)
        .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
      
      // Calculate upcharge quantity (2XL+) from processed sizes
      const upchargeQuantity = line.type === LINE_TYPES.DESIGN_ONLY ? 0 : (
        Number(processedSizes['2XL'] || 0) +
        Number(processedSizes['3XL'] || 0) +
        Number(processedSizes['4XL'] || 0) +
        Number(processedSizes['5XL'] || 0)
      );
      
      const price = parseFloat(line.price) || 0;
      const cogs = parseFloat(line.cogs) || 0;
      const upchargeAmount = (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice);
      
      // Design Only: price is per unit, line total = quantity × price. Others: same plus upcharges.
      const linePrice = line.type === LINE_TYPES.DESIGN_ONLY
        ? totalQuantity * price
        : (totalQuantity * price) + (upchargeQuantity * upchargeAmount);
      
      return {
        quantity: totalQuantity,
        linePrice: linePrice,
        lineCOGS: totalQuantity * cogs,
        upchargeQuantity
      };
    });

    // Calculate totals
    const totalQuantity = lineTotals.reduce((sum, line) => sum + line.quantity, 0);
    const upchargeAmount = (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice);
    const totalUpcharges = lineTotals.reduce((sum, line) => sum + (line.upchargeQuantity * upchargeAmount), 0);
    const subtotal = lineTotals.reduce((sum, line) => sum + line.linePrice, 0);
    const totalCOGS = lineTotals.reduce((sum, line) => sum + line.lineCOGS, 0);

    // Calculate fees from the new fees array
    const feesTotal = calculateTotalFees();

    // Calculate subtotal before discount
    const subtotalBeforeDiscount = subtotal + feesTotal;

    // Calculate discount
    let discountAmount = 0;
    if (orderData.discount && orderData.discount.amount) {
      if (orderData.discount.type === 'percent') {
        discountAmount = subtotalBeforeDiscount * (parseFloat(orderData.discount.amount) / 100);
      } else {
        discountAmount = parseFloat(orderData.discount.amount) || 0;
      }
    }

    const taxableFeesTotal = sumTaxableFeesAmount(fees);
    const salesTax = computeSalesTax({
      subtotal,
      feesTotal,
      taxableFeesTotal,
      discountAmount,
      isTaxExempt: Boolean(additionalInstructions?.taxExempt)
    });

    // Calculate final totals
    const orderTotal = subtotalBeforeDiscount - discountAmount;
    const cogsPercentage = orderTotal > 0 ? ((totalCOGS / orderTotal) * 100) : 0;

    // Calculate print minutes and revenue
    const printMinutes = lines.reduce((total, line) => total + calculatePrintMinutes(line), 0);
    const printRevenue = lines.reduce((total, line) => total + calculatePrintRevenue(line), 0);
    const pressRate = calculatePressRate(printRevenue, printMinutes);

    return {
      subtotal: Number(subtotal.toFixed(2)),
      feesTotal: Number(feesTotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      orderTotal: Number(orderTotal.toFixed(2)),
      cogsTotal: Number(totalCOGS.toFixed(2)),
      cogsPercentage: Number(cogsPercentage.toFixed(1)),
      totalQuantity: lines.reduce((sum, line) => {
        // Sum up all quantities from all lines
        return sum + Object.values(line.sizes || {})
          .reduce((lineSum, qty) => lineSum + (Number(qty) || 0), 0);
      }, 0),
      totalLines: lines.length,
      totalUpcharges: Number(totalUpcharges.toFixed(2)),
      upchargePrice: (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice),
      printMinutes,
      printRevenue,
      pressRate,
      salesTax: Number(salesTax.toFixed(2))
    };
  };

  // Memoize the pricing calculations
  const pricing = useMemo(() => calculatePricing(), [
    orderData.fee1Price,
    orderData.fee1Quantity,
    orderData.fee2Price,
    orderData.fee2Quantity,
    orderData.upchargePrice,
    // Add any other values that should trigger a recalculation
    orderData.lineItems
  ]);

  // Memoize the total pieces calculation
  const totalPieces = useMemo(() => calculateTotalPieces(), [
    lines // Watch the lines array for changes
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // ... existing submit logic ...
    
    // Remove the onClose call
    // After successful submission, maybe navigate instead?
    // navigate('/orders');  // Uncomment if you want to redirect after submit
  };

  const handleRemoveLine = (index) => {
    const newLines = [...lines];
    newLines.splice(index, 1);
    setLines(newLines);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <CustomerSearchContainer>
          <SearchInput
            type="text"
            value={customerSearch}
            onChange={handleCustomerSearch}
            onBlur={handleCustomerSearchBlur}
            placeholder="Search customers..."
            onKeyDown={handleKeyDown}
          />
          {filteredCustomers.length > 0 ? (
            <SearchResults>
              {filteredCustomers.map(customer => (
                <SearchResultItem
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  {customer.client}
                </SearchResultItem>
              ))}
            </SearchResults>
          ) : (customerSearch.length > 0 && !orderData.customerName) ? (
            <SearchResults>
              <AddNewButton onClick={() => setShowAddCustomer(true)}>
                + Add New Customer
              </AddNewButton>
            </SearchResults>
          ) : null}
        </CustomerSearchContainer>

        <SelectContainer>
          <ProjectManagerSelect
            value={orderData.projectManager}
            onChange={(e) => setOrderData({...orderData, projectManager: e.target.value})}
          >
            <option key="pm-default" value="">SELECT PM</option>
            {Array.isArray(projectManagers) && deduplicatePMs(projectManagers).map((pm, index) => {
              const key = generatePMKey(pm, index);
              const value = typeof pm === 'object' ? pm.name : pm;
              return (
                <option key={key} value={value}>
                  {value}
                </option>
              );
            })}
          </ProjectManagerSelect>
        </SelectContainer>

        <FacilitySelectContainer>
          <ProjectManagerSelect
            value={resolveFacilityId(orderData.facilityId || activeFacilityId)}
            onChange={(e) => setOrderData({ ...orderData, facilityId: e.target.value })}
            aria-label="Production Facility"
            title="Production Facility"
          >
            {FACILITIES.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </ProjectManagerSelect>
        </FacilitySelectContainer>

        <DateContainer>
          <div>
            <label>Start Date</label>
            <DateInput
              type="date"
              value={orderData.startDate}
              onChange={(e) => setOrderData({...orderData, startDate: e.target.value})}
            />
          </div>
          <div>
            <label>Due Date</label>
            <DateInput
              type="date"
              value={orderData.dueDate}
              onChange={(e) => setOrderData({...orderData, dueDate: e.target.value})}
            />
          </div>
        </DateContainer>
      </HeaderSection>

      <LinesSection>
        {lines.map((line, index) => (
          <LineItem2
            key={line.id}
            lineData={line}
            index={index}
            isExpanded={line.isExpanded}
            customerId={effectiveCustomerId}
            onUpdate={(updatedLine) => handleLineUpdate(index, updatedLine)}
            onDelete={() => handleRemoveLine(index)}
            onMoveUp={() => handleMoveLine(index, -1)}
            onMoveDown={() => handleMoveLine(index, 1)}
            canMoveUp={index > 0}
            canMoveDown={index < lines.length - 1}
            onDuplicate={() => {
              setLines((prevLines) => {
                const source = prevLines[index];
                if (!source) return prevLines;
                const duplicate = duplicateLineItem(source, { isExpanded: true });
                return [...prevLines, duplicate];
              });
            }}
          />
        ))}
        <AddLineButtonContainer>
          <AddLineButton onClick={handleAddLine}>
            Add Line
          </AddLineButton>
        </AddLineButtonContainer>
      </LinesSection>

      <FooterSection>
        {/* Notes Column */}
        <NotesFooterBox>
          <NotesBox
            value={orderData.notes}
            onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
            placeholder="Order Notes..."
          />
        </NotesFooterBox>

        {/* +2XL Upcharge Column */}
        <UpchargeFooterBox>
          <div style={{ padding: '4px', fontWeight: 600, fontSize: '0.85em' }}>+2XL Upcharge</div>
          <FooterRow>
            <span className="label">QTY:</span>
            <input
              type="text"
              value={calculate2XLPlus() || 0}
              readOnly
              style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }}
            />
          </FooterRow>
          <FooterRow>
            <span className="label">$:</span>
            <input
              type="text"
              value={orderData.upchargePrice === '' || orderData.upchargePrice == null ? '2.00' : orderData.upchargePrice}
              onChange={(e) => setOrderData({...orderData, upchargePrice: e.target.value})}
            />
          </FooterRow>
          <FooterRow>
            <span className="label">Total:</span>
            <input
              type="text"
              value={((calculate2XLPlus() || 0) * (parseFloat(orderData.upchargePrice) || 0)).toFixed(2)}
              readOnly
              style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }}
            />
          </FooterRow>
        </UpchargeFooterBox>

        {/* Fees, Discounts & Instructions Column */}
        <FeesFooterBox>
          <div className="header">Fees, Discounts & Instructions</div>
          <div className="summary">
            {/* Fees */}
            {fees.filter(fee => fee.type && fee.quantity && fee.price).map((fee) => (
              <div className="fee-line" key={fee.id}>
                <span className="fee-type">{fee.type}</span>
                <span className="fee-amount">${(parseFloat(fee.quantity) * parseFloat(fee.price)).toFixed(2)}</span>
              </div>
            ))}
            {/* Discount */}
            {orderData.discount && orderData.discount.amount && (
              <div className="fee-line">
                <span className="fee-type">Discount ({orderData.discount.type === 'percent' ? `${orderData.discount.amount}%` : 'Fixed'})</span>
                <span className="fee-amount" style={{ color: '#FF6B6B' }}>
                  -${calculatePricing().discountAmount.toFixed(2)}
                </span>
              </div>
            )}
            {/* Instructions */}
            {hasActiveInstructions(additionalInstructions) && (
              <div className="instructions">
                {additionalInstructions.shipping && <span className="instruction-tag">Shipping</span>}
                {additionalInstructions.sorting && <span className="instruction-tag">Sorting</span>}
                {additionalInstructions.folding && <span className="instruction-tag">Folding</span>}
                {additionalInstructions.bagging && <span className="instruction-tag">Bagging</span>}
                {additionalInstructions.fulfillment && <span className="instruction-tag">Fulfillment</span>}
                {additionalInstructions.neckTag && <span className="instruction-tag">Neck Tag</span>}
                {additionalInstructions.taxExempt && <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt</span>}
                {additionalInstructions.designWorkOnly && <span className="instruction-tag" style={{ background: '#9C27B0' }}>Design Only</span>}
                {additionalInstructions.doNotBill && <span className="instruction-tag" style={{ background: '#f44336' }}>Do Not Bill</span>}
                {additionalInstructions.followUpEmail && <span className="instruction-tag" style={{ background: '#2196F3' }}>Follow-Up Email</span>}
                {additionalInstructions.instagramWorthy && <span className="instruction-tag">Instagram Worthy</span>}
                {additionalInstructions.providedGoods && <span className="instruction-tag" style={{ background: '#5B9BD5' }}>Provided Goods</span>}
                {additionalInstructions.sample && <span className="instruction-tag" style={{ background: '#8E6BBE' }}>Sample</span>}
                {additionalInstructions.other && <span className="instruction-tag" style={{ background: '#607D8B' }}>{additionalInstructions.other}</span>}
              </div>
            )}
            {/* Empty state */}
            {fees.filter(fee => fee.type && fee.quantity && fee.price).length === 0 && 
             !(orderData.discount && orderData.discount.amount) && 
             !hasActiveInstructions(additionalInstructions) && (
              <span style={{ fontStyle: 'italic' }}>No fees, discounts, or instructions</span>
            )}
          </div>
          <EditFeesBtn onClick={() => setShowFeesModal(true)}>
            Edit
          </EditFeesBtn>
        </FeesFooterBox>

        {/* Totals Column */}
        <TotalsFooterBox>
          <div className="header">TOTALS</div>
          <FooterRow>
            <span className="label">PCS:</span>
            <input type="text" value={calculateTotalPieces()} readOnly />
          </FooterRow>
          <FooterRow>
            <span className="label">$:</span>
            <input
              type="text"
              value={(() => {
                const pricing = calculatePricing();
                const orderTotal = pricing.orderTotal;
                const taxExempt = additionalInstructions.taxExempt;
                const tax = taxExempt ? 0 : pricing.salesTax;
                return tax > 0 ? `$${orderTotal.toFixed(2)} + $${tax.toFixed(2)}/tax` : `$${orderTotal.toFixed(2)}`;
              })()}
              readOnly
            />
          </FooterRow>
          <FooterRow>
            <span className="label">Stats:</span>
            <input 
              type="text" 
              value={`${calculatePricing().cogsPercentage.toFixed(1)}% / ${formatMinutes(calculatePricing().printMinutes)} ${(() => {
                const rate = calculatePricing().pressRate;
                if (rate >= 350) return '🟢';
                if (rate >= 300) return '🟡';
                return '🔴';
              })()}`}
              readOnly 
            />
          </FooterRow>
          <CreateOrderButton 
            onClick={handleSave} 
            disabled={isSubmitting}
            style={{ marginTop: 'auto' }}
          >
            {isSubmitting ? 'Creating...' : 'Create Order'}
          </CreateOrderButton>
        </TotalsFooterBox>
      </FooterSection>

      {showSuccess && <SuccessAnimation onComplete={() => setShowSuccess(false)} />}
      {showEmbleoPrompt ? (
        <EmbleoPromptOverlay>
          <EmbleoPromptModal>
            <EmbleoPromptTitle>The Job Contains Embroidery</EmbleoPromptTitle>
            <EmbleoPromptText>Send to Embleo now?</EmbleoPromptText>
            {embleoSendError ? (
              <div style={{ color: '#ff6b6b', fontSize: '0.7rem', marginBottom: 10 }}>{embleoSendError}</div>
            ) : null}
            <EmbleoPromptActions>
              <EmbleoPromptButton
                type="button"
                onClick={() => handleEmbleoDecision(false)}
                disabled={isSendingToEmbleo}
              >
                No
              </EmbleoPromptButton>
              <EmbleoPromptButton
                primary
                type="button"
                onClick={() => handleEmbleoDecision(true)}
                disabled={isSendingToEmbleo}
              >
                {isSendingToEmbleo ? 'Sending...' : 'Yes'}
              </EmbleoPromptButton>
            </EmbleoPromptActions>
          </EmbleoPromptModal>
        </EmbleoPromptOverlay>
      ) : null}

      <AddCustomer
        isOpen={showAddCustomer}
        onClose={() => setShowAddCustomer(false)}
        onAdd={handleAddNewCustomer}
        initialData={{ client: customerSearch }}
      />

      <FeesModal
        isOpen={showFeesModal}
        onClose={() => setShowFeesModal(false)}
        onSave={handleFeesModalSave}
        enableShipping
        shippingContext={{
          customerName: orderData.customerName || '',
          orderReference: orderData.poNumber ? String(orderData.poNumber) : '',
          customerReference: orderData.customerId || ''
        }}
        initialData={{
          fees,
          discount: orderData.discount || { type: 'fixed', amount: '' },
          additionalInstructions,
          upchargePrice: orderData.upchargePrice,
          customerPoNumber: orderData.customerPoNumber || '',
          shippingDetails
        }}
      />
    </PageContainer>
  );
};

export default NewOrder; 